import * as React from 'react';
import { WaiverFaabEvent, Player, Team, PlayerClaimResult, PlayerClaimResultResultEnum } from '../../../sdk/model';
import {Box, Typography, TableContainer, Paper, Table, TableHead, TableRow, TableBody, Card, CardContent, Chip, useMediaQuery} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import PlayerCardLink from '../../cards/playerCardLink';
import { toSalary } from '../../util/SalaryDisplay';
import TeamDisplay from '../team/TeamDisplay';

interface waiverFaabEventQuickViewProps {
  event: WaiverFaabEvent,
  playerMap: Map<string | undefined, Player | undefined>,
  teamMap: Map<string | undefined, Team | undefined>
}

function WaiverFaabEventQuickView(props: waiverFaabEventQuickViewProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Cast to proper type for waiver/faab events
  const waiverEvent = props.event as any;
  const waiverResults : PlayerClaimResult[] = waiverEvent.waiverResults;
  const winningWaiverClaims = waiverResults.filter(result => result.result === PlayerClaimResultResultEnum.Success);
  const waiverPlayerResults = winningWaiverClaims.map(winningClaim => {
    return {
      playerId: winningClaim.bid!.playerId!,
      winningTeamId: winningClaim.teamId!,
      dropPlayerIds: winningClaim.bid!.dropPlayerIds,
      losingTeamIds: waiverResults.filter(result => 
          result.result !== PlayerClaimResultResultEnum.Success && 
          result.bid!.playerId === winningClaim.bid!.playerId
        ).map(result => result.teamId!)
    }
  });
  const faabResults : PlayerClaimResult[] = waiverEvent.faabResults;
  const winningFaabClaims = faabResults.filter(result => result.result === PlayerClaimResultResultEnum.Success);
  const faabPlayerResults = winningFaabClaims.map(winningClaim => {
    return {
      playerId: winningClaim.bid!.playerId!,
      type: winningClaim.bid!.type,
      winningBid: {
        teamId: winningClaim.teamId!,
        bidAmount: (winningClaim.bid as any)!.bidAmount,
        dropPlayerIds: winningClaim.bid!.dropPlayerIds,
        practiceSquad: (winningClaim.bid as any)!.practiceSquad
      },
      losingBids: faabResults.filter(result => 
          result.result !== PlayerClaimResultResultEnum.Success && 
          result.bid!.playerId === winningClaim.bid!.playerId
        ).map(result => {
          return {
            teamId: result.teamId!,
            bidAmount: (result.bid as any)!.bidAmount
          }
        })
    }
  });
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));
  // Mobile component for waiver claims
  const renderWaiverClaimsMobile = () => (
    <Box>
      <Typography variant="h6" component="div" color="text.secondary" sx={{ mb: 2 }}>
        Claimed on Waivers:
      </Typography>
      {waiverPlayerResults.map(claimResult => {
        const player = props.playerMap.get(claimResult.playerId)!;
        const winningTeam = props.teamMap.get(claimResult.winningTeamId)!;
        return (
          <Card key={claimResult.playerId} sx={{ mb: 2, borderLeft: 4, borderLeftColor: theme.palette.success.main }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  <PlayerCardLink player={player} leagueId={waiverEvent.leagueId} />
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Chip label={`Won by ${winningTeam.name}`} color="success" size="small" />
              </Box>
              {claimResult.dropPlayerIds!.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Dropped:</strong> {claimResult.dropPlayerIds!.map((dropPlayerId, index) => {
                    const dropPlayer = props.playerMap.get(dropPlayerId)!;
                    return (
                      <React.Fragment key={dropPlayerId}>
                        <PlayerCardLink player={dropPlayer} leagueId={waiverEvent.leagueId} />
                        {index < claimResult.dropPlayerIds!.length - 1 && ", "}
                      </React.Fragment>
                    );
                  })}
                </Typography>
              )}
              {claimResult.losingTeamIds.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Other bidders:</strong> {claimResult.losingTeamIds.map((teamId, index) => {
                    const losingTeam = props.teamMap.get(teamId)!;
                    return (
                      <React.Fragment key={teamId}>
                        {losingTeam.name}
                        {index < claimResult.losingTeamIds.length - 1 && ", "}
                      </React.Fragment>
                    );
                  })}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );

  // Desktop component for waiver claims
  const waiverClaimsDesktop = React.useMemo(() => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" component="div" color="text.secondary" sx={{ flexGrow: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label="Claimed on Waivers" color="success" variant="outlined" />
      </Typography>
      <TableContainer component={Paper} sx={{ borderLeft: 4, borderLeftColor: theme.palette.success.main }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead sx={{fontSize : 20}}>
            <TableRow>
              <StyledTableCell>Player</StyledTableCell>
              <StyledTableCell>Winning Team</StyledTableCell>
              <StyledTableCell>Dropped Player(s)</StyledTableCell>
              <StyledTableCell>Other Bidding Teams</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {waiverPlayerResults.map(claimResult => {
              const player = props.playerMap.get(claimResult.playerId)!;
              const winningTeam = props.teamMap.get(claimResult.winningTeamId)!;
              return <TableRow key={claimResult.playerId} sx={{ '&:hover': { backgroundColor: theme.palette.success.light + '08' } }}>
                <StyledTableCell><PlayerCardLink player={player} leagueId={waiverEvent.leagueId} /></StyledTableCell>
                <StyledTableCell>
                  <Chip label={winningTeam.name} color="success" size="small" />
                </StyledTableCell>
                <StyledTableCell>
                  {claimResult.dropPlayerIds!.length === 0 ? (
                    <Chip label="None" variant="outlined" size="small" />
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {claimResult.dropPlayerIds!.map(dropPlayerId => {
                        const dropPlayer = props.playerMap.get(dropPlayerId)!;
                        return <PlayerCardLink key={dropPlayerId} player={dropPlayer} leagueId={waiverEvent.leagueId} />
                      })}
                    </Box>
                  )}
                </StyledTableCell>
                <StyledTableCell>
                   {claimResult.losingTeamIds.length ? (
                     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                       {claimResult.losingTeamIds.map(teamId => {
                         const losingTeam = props.teamMap.get(teamId)!;
                         return <Chip key={teamId} label={losingTeam.name} variant="outlined" size="small" />
                       })}
                     </Box>
                   ) : <Chip label="None" variant="outlined" size="small" />}
                </StyledTableCell>
              </TableRow>
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ),[]);
  
  const faabClaimsDesktop = React.useMemo(() => (
    <TableContainer component={Paper} sx={{ borderLeft: 4, borderLeftColor: theme.palette.warning.main }}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead sx={{fontSize : 20}}>
          <TableRow>
            <StyledTableCell>Player</StyledTableCell>
            <StyledTableCell>Winning Bid</StyledTableCell>
            <StyledTableCell>Dropped Player(s)</StyledTableCell>
            <StyledTableCell>Other Bids</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {faabPlayerResults.map(claimResult => {
            const player = props.playerMap.get(claimResult.playerId)!;
            const winningTeam = props.teamMap.get(claimResult.winningBid.teamId)!;
            return <TableRow key={claimResult.playerId} sx={{ '&:hover': { backgroundColor: theme.palette.warning.light + '08' } }}>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayerCardLink player={player} leagueId={waiverEvent.leagueId} />
                  {claimResult.type === 'poach_claim' && <Chip label="POACH" color="error" size="small" />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={winningTeam.name} color="warning" size="small" />
                  <Chip label={toSalary(claimResult.winningBid.bidAmount)} color="primary" size="small" />
                  {claimResult.winningBid.practiceSquad && <Chip label="PS" variant="outlined" size="small" />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                {claimResult.winningBid.dropPlayerIds!.length === 0 ? (
                  <Chip label="None" variant="outlined" size="small" />
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {claimResult.winningBid.dropPlayerIds!.map(dropPlayerId => {
                      const dropPlayer = props.playerMap.get(dropPlayerId)!;
                      return <PlayerCardLink key={dropPlayerId} player={dropPlayer} leagueId={waiverEvent.leagueId} />
                    })}
                  </Box>
                )}
              </StyledTableCell>
              <StyledTableCell>
                 {claimResult.losingBids.length ? (
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                     {claimResult.losingBids.map(losingBid => {
                       const losingTeam = props.teamMap.get(losingBid.teamId)!;
                       return (
                         <Box key={losingBid.teamId} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                           <Chip label={losingTeam.name} variant="outlined" size="small" />
                           <Typography variant="caption">({toSalary(losingBid.bidAmount)})</Typography>
                         </Box>
                       )
                     })}
                   </Box>
                 ) : <Chip label="None" variant="outlined" size="small" />}
              </StyledTableCell>
            </TableRow>
          })}
        </TableBody>
      </Table>
    </TableContainer>
  ), [])

  return <Box>
    {waiverPlayerResults.length > 0 && (isMobile ? renderWaiverClaimsMobile() : waiverClaimsDesktop)}
    {waiverEvent.waiverClearedPlayers.length > 0 && <Box sx={{ mt: 3, mb: 3 }}>
      <Typography variant={isMobile ? "h6" : "h5"} component="div" color="text.secondary" sx={{ flexGrow: 1, mb: isMobile ? 1 : 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {isMobile ? "Cleared Waivers:" : <Chip label="Cleared Waivers" color="info" variant="outlined" />}
      </Typography>
      {isMobile ? (
        <Card sx={{ borderLeft: 4, borderLeftColor: theme.palette.info.main }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {waiverEvent.waiverClearedPlayers.map(playerId => props.playerMap.get(playerId)!).map((player, playerIndex) => (
                <React.Fragment key={player.id}>
                  <PlayerCardLink player={player} leagueId={waiverEvent.leagueId} />
                  {playerIndex < waiverEvent.waiverClearedPlayers.length - 1 && ", "}
                </React.Fragment>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Paper sx={{ p: 2, borderLeft: 4, borderLeftColor: theme.palette.info.main, backgroundColor: theme.palette.info.light + '08' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {waiverEvent.waiverClearedPlayers.map(playerId => props.playerMap.get(playerId)!).map((player, playerIndex) => (
              <React.Fragment key={player.id}>
                <PlayerCardLink player={player} leagueId={waiverEvent.leagueId} />
                {playerIndex < waiverEvent.waiverClearedPlayers.length - 1 && ", "}
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      )}
    </Box>}
    {faabPlayerResults.length > 0 && <Box sx={{ mt: 3 }}>
      <Typography variant={isMobile ? "h6" : "h5"} component="div" color="text.secondary" sx={{ flexGrow: 1, mb: isMobile ? 2 : 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {isMobile ? "Claimed via FAAB:" : <Chip label="Claimed via FAAB" color="warning" variant="outlined" />}
      </Typography>
      {isMobile ? (
        faabPlayerResults.map(claimResult => {
          const player = props.playerMap.get(claimResult.playerId)!;
          const winningTeam = props.teamMap.get(claimResult.winningBid.teamId)!;
          return (
            <Card key={claimResult.playerId} sx={{ mb: 2, borderLeft: 4, borderLeftColor: theme.palette.warning.main }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="primary">
                    <PlayerCardLink player={player} leagueId={waiverEvent.leagueId} />
                    {claimResult.type === 'poach_claim' && <Chip label="POACH" color="error" size="small" sx={{ ml: 1 }} />}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Chip 
                    label={`${toSalary(claimResult.winningBid.bidAmount)}${claimResult.winningBid.practiceSquad ? " (PS)" : ""}`}
                    color="warning" 
                    size="small" 
                  />
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    by <TeamDisplay leagueId={waiverEvent.leagueId} team={winningTeam} emphasized={false} teamId={claimResult.winningBid.teamId} />
                  </Typography>
                </Box>
                {claimResult.winningBid.dropPlayerIds!.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Dropped:</strong> {claimResult.winningBid.dropPlayerIds!.map((dropPlayerId, index) => {
                      const dropPlayer = props.playerMap.get(dropPlayerId)!;
                      return (
                        <React.Fragment key={dropPlayerId}>
                          <PlayerCardLink player={dropPlayer} leagueId={waiverEvent.leagueId} />
                          {index < claimResult.winningBid.dropPlayerIds!.length - 1 && ", "}
                        </React.Fragment>
                      );
                    })}
                  </Typography>
                )}
                {claimResult.losingBids.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Other bids:</strong> {claimResult.losingBids.map((losingBid, index) => {
                      const losingTeam = props.teamMap.get(losingBid.teamId)!;
                      return (
                        <React.Fragment key={losingBid.teamId}>
                          <TeamDisplay leagueId={waiverEvent.leagueId} team={losingTeam} emphasized={false} teamId={losingBid.teamId} /> ({toSalary(losingBid.bidAmount)})
                          {index < claimResult.losingBids.length - 1 && ", "}
                        </React.Fragment>
                      );
                    })}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })
      ) : faabClaimsDesktop
    }
    </Box>}
  </Box>
}

export default WaiverFaabEventQuickView;
